import PersonIcon from '@mui/icons-material/Person';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import TtyIcon from '@mui/icons-material/Tty';
import { Box, Tooltip, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useNumberFormat } from '../hooks/useNumberFormat';
import type { TenantAttendanceMetricsTelephony } from '../types';
import TenantAttendanceDetailsDialog from './TenantAttendanceDetailsDialog';
import {
  TENANT_ATTENDANCE_METRIC_ACCENT_COLOR,
  TENANT_ATTENDANCE_METRIC_INACTIVE_ICON_COLOR,
  TENANT_ATTENDANCE_METRIC_INACTIVE_VALUE_COLOR,
  type TenantAttendanceMetricKey,
} from './tenantAttendanceMetricConfig';

interface TenantAttendanceIndicatorProps {
  telephony: TenantAttendanceMetricsTelephony | null;
}

function clampMetric(value: number | undefined | null): number {
  return Math.max(Number(value || 0), 0);
}

/**
 * Render a compact tenant-wide human-attendance summary for the shell header.
 *
 * The pill keeps the aggregate posture visible in the top bar and opens a
 * richer operational drilldown modal on click.
 */
export default function TenantAttendanceIndicator({
  telephony,
}: TenantAttendanceIndicatorProps) {
  const { t } = useTranslation(['common']);
  const { formatNumber } = useNumberFormat();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const isIdle = useMemo(
    () => (
      clampMetric(telephony?.operatorsAvailableCount) === 0
      && clampMetric(telephony?.operatorsInAttendanceCount) === 0
      && clampMetric(telephony?.waitingHumanAnswerCount) === 0
    ),
    [
      telephony?.operatorsAvailableCount,
      telephony?.operatorsInAttendanceCount,
      telephony?.waitingHumanAnswerCount,
    ],
  );

  const metrics = useMemo(
    () => [
      {
        key: 'available' as TenantAttendanceMetricKey,
        icon: PersonIcon,
        value: clampMetric(telephony?.operatorsAvailableCount),
      },
      {
        key: 'attending' as TenantAttendanceMetricKey,
        icon: RecordVoiceOverIcon,
        value: clampMetric(telephony?.operatorsInAttendanceCount),
      },
      {
        key: 'unavailable' as TenantAttendanceMetricKey,
        icon: PersonOffIcon,
        value: clampMetric(telephony?.operatorsUnavailableCount),
      },
      {
        key: 'waiting' as TenantAttendanceMetricKey,
        icon: PhoneCallbackIcon,
        value: clampMetric(telephony?.waitingHumanAnswerCount),
      },
    ],
    [
      telephony?.operatorsAvailableCount,
      telephony?.operatorsInAttendanceCount,
      telephony?.operatorsUnavailableCount,
      telephony?.waitingHumanAnswerCount,
    ],
  );

  return (
    <>
      <Tooltip title={t('common:subscription.headerAttendance.tooltip')} arrow>
        <Box
          component="button"
          type="button"
          data-testid="tenant-attendance-indicator-trigger"
          onClick={() => setDetailsOpen(true)}
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            gap: 1,
            opacity: isIdle ? 0.4 : 1,
            p: 0,
            border: 0,
            background: 'transparent',
            color: 'inherit',
            cursor: 'pointer',
            textAlign: 'left',
            transition: 'opacity 160ms ease',
          }}
        >
          <TtyIcon
            data-testid="tenant-attendance-indicator-shell-icon"
            sx={{
              color: 'text.secondary',
              fontSize: 20,
              flexShrink: 0,
            }}
          />
          <Box
            data-testid="tenant-attendance-indicator-root"
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: 40,
              px: 1.1,
              borderRadius: '10px',
              border: '1px solid rgba(255, 255, 255, 0.28)',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          >
            {metrics.map((metric) => {
              const MetricIcon = metric.icon;
              const accentColor = TENANT_ATTENDANCE_METRIC_ACCENT_COLOR[metric.key];
              const isActive = metric.value > 0;
              return (
                <Box
                  key={metric.key}
                  data-testid={`tenant-attendance-indicator-metric-${metric.key}`}
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: 0,
                    p: '8px 8px 8px 14px',
                  }}
                >
                  <MetricIcon
                    data-testid={`tenant-attendance-indicator-icon-${metric.key}`}
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: '7px',
                      fontSize: 12,
                      color: isActive ? accentColor : TENANT_ATTENDANCE_METRIC_INACTIVE_ICON_COLOR,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    data-testid={`tenant-attendance-indicator-value-${metric.key}`}
                    sx={{
                      color: isActive ? accentColor : TENANT_ATTENDANCE_METRIC_INACTIVE_VALUE_COLOR,
                      fontFamily: '"Barlow Condensed", "Oswald", sans-serif',
                      fontSize: '1.3rem',
                      fontWeight: 700,
                      lineHeight: 1,
                      fontVariantNumeric: 'tabular-nums',
                      userSelect: 'none',
                    }}
                  >
                    {formatNumber(metric.value)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Tooltip>
      <TenantAttendanceDetailsDialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        telephony={telephony}
      />
    </>
  );
}
